/* IncidentChatModal.js — CSS-driven modal (no Bootstrap JS), safe backdrop handling
   - Keeps your polling, actions, and <Messages /> props intact
   - Removes window.bootstrap.Modal usage to avoid stuck backdrops / freezes
*/

import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { hideModal } from "../../actions/modal";
import { putCloseChat } from "../../actions/reportSummaries";
import {
  fetchMessages,
  postChatMessage,
  updatePendingMessage,
  clearPendingMessage,
  clearMessages,
} from "../../actions/messages";
import Messages from "../../views/Messages/Messages";
import "./IncidentChatModal.css";

const IncidentChatModal = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const modalRef = useRef(null);
  const timeoutRef = useRef(null);

  const visible = useSelector((state) => state.modal?.visible || false);
  const modalProps = useSelector((state) => state.modal?.modalProps || {});
  const messages = useSelector((state) => state.messages || {});
  const pendingMessage = messages.pendingMessage || "";
  const profile = useSelector((state) => state.profile);

  const hasProfile =
    profile && typeof profile.sub === "string" && profile.sub !== "";

  const { incident, agencyId, reportSchemaId } = modalProps;

  // --- helpers --------------------------------------------------------------

  // Kill *any* Bootstrap-inserted backdrops or scroll locks
  const nukeBootstrapArtifacts = () => {
    // remove ALL bootstrap backdrops if they exist
    document.querySelectorAll(".modal-backdrop").forEach((n) => n.remove());
    // remove any scroll lock classes/styles Bootstrap might have added
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  };

  const hide = () => {
    nukeBootstrapArtifacts(); // clean first
    dispatch(hideModal());
  };

  // REPLACE your current closeChat with this:
const closeChat = async () => {
  // if we somehow lack ids, just close the modal
  if (!incident?._id) { hide(); return; } // safety

  try {
    // Some versions of putCloseChat needed a token; some didn’t.
    // We fetch a token but only pass it if the action expects >= 4 args.
    let token = null;
    try {
      token = await getAccessTokenSilently();
    } catch (e) {
      // no token? fine—action may not need it
      console.warn("[EndChat] token not available; proceeding without it");
    }

    if (typeof putCloseChat === "function") {
      // Length check = number of declared params on the action creator
      if (putCloseChat.length >= 4 && token) {
        await dispatch(putCloseChat(incident._id, agencyId, reportSchemaId, token));
      } else {
        await dispatch(putCloseChat(incident._id, agencyId, reportSchemaId));
      }
    } else {
      console.error("[EndChat] putCloseChat is not a function");
    }
    
  } catch (err) {
    console.error("[EndChat] dispatch failed:", err);
    // Optionally keep the modal open on error; your call. For now we still close:
  } finally {
    // Always hide after we attempt to close on the server
    hide();
    prepareFetch(); // makes blue dot faster by refresh no await
  }
};

  const prepareFetch = async () => {
    try {
      const token = await getAccessTokenSilently();
      dispatch(fetchMessages(incident._id, agencyId, reportSchemaId, token));
    } catch (err) {
      console.error("Token fetch failed in prepareFetch:", err);
    }
  };

  const addMessage = async () => {
    if (!pendingMessage?.trim() || !hasProfile || !incident?._id) return;

    const message = {
      type: "chat",
      display_name: profile.user_metadata?.name || profile.name,
      message: pendingMessage,
      sender_id: profile.sub,
      sent: Date.now(),
    };

    try {
      const token = await getAccessTokenSilently();
     await dispatch(
        postChatMessage(incident._id, agencyId, reportSchemaId, message, token)
      );
      dispatch(clearPendingMessage());
    } catch (err) {
      console.error("❌ Failed to get token in addMessage:", err);
    }
  };

  // --- effects --------------------------------------------------------------

  // Fetch immediately when opening
  useEffect(() => {
    if (visible && incident?._id) {
      console.log("[ChatModal] opening → fetch now");
      prepareFetch();
    }
  }, [visible, incident?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 10s while visible (changed to 5s to make faster in chat)
  useEffect(() => {
    if (!visible || !incident?._id) return;
    const id = setInterval(() => {
      prepareFetch();
    //}, 10000);
    }, 4000);
    return () => clearInterval(id);
  }, [visible, incident?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always clean stray artifacts when opening/closing/unmounting
  useEffect(() => {
    if (visible) {
      nukeBootstrapArtifacts(); // ensure clean slate on open
    }
    return () => {
      clearTimeout(timeoutRef.current);
      nukeBootstrapArtifacts();
    };
  }, [visible]);

  if (!visible || !incident?._id) return null;

  // --- render ---------------------------------------------------------------

  return (
    <div>
      {/* Our own backdrop (under the modal). Safe and fully controlled by us. */}
      <div className="chat-modal-backdrop fade show" />

      {/* Pure CSS modal (no Bootstrap JS). Always visible when rendered. */}
      {/* NOTE: no "fade" to avoid transition timing issues */}
      <div
        id="chat-modal"
        ref={modalRef}
        className="modal show d-block chat-modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="chatModalLabel"
        aria-modal="true"
      >
        <div className="chat-modal-dialog modal-dialog-centered" role="document">
          <div className="chat-modal-content">
            <div className="chat-modal-header">
              <h5
                className="chat-modal-title d-flex align-items-center"
                id="chatModalLabel"
              >
                <i className="material-icons me-2">chat</i>
                <span>
                  Chat - {incident.display_title || "Incident # Unknown"} -{" "}
                  {incident.title || "Untitled"}
                </span>
              </h5>
              {/* just closes via Redux; no Bootstrap JS call */}
              <button
                type="button"
                className="chat-modal-close"
                onClick={hide}
                aria-label="Close"
              >
                Close X
              </button>
            </div>

            <div className="chat-modal-body" id="chat-modal-body">
              <Messages
                closeTitle="End Chat"
                incident={incident}
                messages={messages.messages.filter(
                  (message) => message.type === "chat"
                )}
                pendingMessage={pendingMessage}
                hide={hide}
                clearMessages={clearMessages}
                updatePendingMessage={updatePendingMessage}
                addMessage={addMessage}
                closeModal={closeChat}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentChatModal;
